import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, formatSgd, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend,
} from "recharts";
import { Wallet, Sparkles, Landmark, Leaf, ShieldAlert, Recycle, TrendingUp, Banknote, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import ICPOffset from "./ICPOffset";
import FeatureGate from "../components/FeatureGate";

function Stat({ label, value, unit, sub, icon: Icon, testid, tone, pkey, pp, pyear }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} pp={pp} year={pyear} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function FinanceOffset() {
  const [d, setD] = useState(null);
  const [opps, setOpps] = useState([]);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/finance-offset/dashboard").then((r) => setD(r.data)).catch(() => setD({ empty: true }));
    api.get("/valueprint/finance-opportunities").then((r) => setOpps(r.data.opportunities)).catch(() => {});
  }, []);
  const genAi = async () => { setBusy(true); try { const r = await api.post("/finance-offset/ai/finance-summary"); setAi(r.data.summary); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading finance…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No financial data yet.</h2></div></div>;

  const tax = d.carbon_tax, icp = d.internal_carbon_price, off = d.offsets, fin = d.green_financing;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="finance-offset-page">
      <div>
        <div className="label-eyebrow mb-1">Finance & Offset</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Wallet className="h-8 w-8 text-accent" /> Finance & Offset
          <Link to="/financing/guide#finance-offset" title="Open the module user guide" data-testid="fo-guide-link"
            className="text-muted-foreground/50 hover:text-accent transition-colors">
            <HelpCircle className="h-4 w-4" />
          </Link>
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Financial view of carbon liabilities — carbon-tax exposure, internal carbon price, offset utilisation, carbon-cost projections and green-financing links.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Carbon tax (2024)" value={formatSgd(tax.current_liability_sgd)} unit="" icon={Landmark} testid="fo-stat-tax" pkey="sys.carbon_tax" pyear={2024} tone={tax.is_taxable ? "text-[#FFB800]" : ""} sub={tax.is_taxable ? "Taxable" : "Below 25k threshold"} />
        <Stat label="Internal carbon charge" value={formatSgd(icp.annual_charge_sgd)} unit="/yr" icon={Banknote} testid="fo-stat-icp" pkey="icp.charge" sub={`@ S$${icp.price_sgd}/t`} />
        <Stat label="Offset coverage" value={off.coverage_pct} unit="%" icon={Recycle} testid="fo-stat-offset" pkey="offsets.coverage" sub={`${formatNum(off.retired)}t retired`} />
        <Stat label="Residual emissions" value={formatNum(off.residual_after_offsets_tco2e)} unit="tCO₂e" icon={Leaf} pkey="offsets.residual" sub="FY2024 Scopes 1+2 − retired offsets" />
        <Stat label="Green financing" value={formatNum(fin.co2_avoided_tonnes)} unit="tCO₂e" icon={TrendingUp} testid="fo-stat-financing" pkey="sys.green_financing" sub={`${fin.cases} cases${fin.requested_quantum_sgd ? ` · ${formatSgd(fin.requested_quantum_sgd)} requested` : ""}`} />
        <Stat label="Risk Register exposure" value={formatSgd(d.financial_risk.total_exposure_sgd)} unit="" icon={ShieldAlert} pkey="risk.exposure" tone={d.financial_risk.total_exposure_sgd ? "text-red-500" : ""} sub={`${d.financial_risk.count ?? d.financial_risk.items.length} risks`} />
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="fo-icp-note">
        Basis: FY2024 calendar-year emissions, Scopes 1+2. Summary figures above are included free in every plan — the full ICP &amp; Offset workspace is a plan add-on.
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="fo-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="projection" data-testid="fo-tab-projection">Carbon Cost Projection</TabsTrigger>
          <TabsTrigger value="risk" data-testid="fo-tab-risk">Financial Risk</TabsTrigger>
          <TabsTrigger value="icp" data-testid="fo-tab-icp">ICP &amp; Offset</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4 space-y-3">
              <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Carbon-tax position (Singapore CPA)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Scope 1 emissions</span><span className="font-mono">{formatNum(tax.scope1_tco2e)} tCO₂e</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Taxable status</span><span className={tax.is_taxable ? "text-[#FFB800]" : "text-[#00E65B]"}>{tax.is_taxable ? "Taxable" : "Below 25,000 t threshold"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Liability @ S$25 (2024)</span><span className="font-mono">{formatSgd(tax.current_liability_sgd)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Liability @ S$45 (2026)</span><span className="font-mono">{formatSgd(tax.liability_2026_sgd)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Liability @ S$65 (2030)</span><span className="font-mono">{formatSgd(tax.liability_2030_sgd)}</span></div>
              </div>
            </div>
            <div className="card-flat p-4 space-y-3">
              <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><Recycle className="h-3.5 w-3.5" /> Offset utilisation</div>
              <div className="flex justify-between text-xs mb-1"><span>Scope 1+2 covered by retired credits</span><span className="font-mono">{off.coverage_pct}%</span></div>
              <Progress value={off.coverage_pct} className="h-1.5" />
              <div className="grid grid-cols-3 gap-2 pt-1 text-center">
                <div className="border border-border rounded-sm p-2"><div className="font-mono text-lg font-bold text-[#00E65B]">{formatNum(off.retired)}</div><div className="text-[10px] text-muted-foreground">Retired</div></div>
                <div className="border border-border rounded-sm p-2"><div className="font-mono text-lg font-bold text-[#FFB800]">{formatNum(off.purchased)}</div><div className="text-[10px] text-muted-foreground">Purchased</div></div>
                <div className="border border-border rounded-sm p-2"><div className="font-mono text-lg font-bold">{formatNum(off.available)}</div><div className="text-[10px] text-muted-foreground">Available</div></div>
              </div>
              <div className="flex justify-between text-xs pt-1"><span className="text-muted-foreground">Portfolio cost</span><span className="font-mono">{formatSgd(off.portfolio_cost_sgd)}</span></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Financing CAPEX" value={formatNum(fin.capex_sgd / 1000, { decimals: 0 })} unit="K S$" icon={Banknote} pkey="gf.pipeline" />
            <Stat label="Annual savings" value={formatNum(fin.annual_savings_sgd / 1000, { decimals: 0 })} unit="K S$" icon={TrendingUp} pkey="sys.green_financing" />
            <Stat label="ICP chargeable base" value={formatNum(icp.chargeable_tco2e)} unit="tCO₂e" icon={Leaf} pkey="icp.charge" />
            <Stat label="ICC-eligible credits" value={formatNum(off.icc_eligible)} unit="tCO₂e" icon={Recycle} pkey="offsets.retired" />
          </div>
          {!!opps.length && (
            <div className="card-flat p-4" data-testid="vp-finance-opportunities">
              <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Leaf className="h-3.5 w-3.5 text-accent" /> Fundable opportunities — tagged by your Lestari ValuePrint</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {opps.map((o) => (
                  <div key={o.id} className="border border-border rounded-sm p-3" data-testid={`vp-fin-opp-${o.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium">{o.title}</div>
                      <Badge variant="lime" className="text-[9px] shrink-0">{o.instrument}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">{o.description}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(o.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI finance summary</div>
              <Button size="sm" variant="primary" onClick={genAi} disabled={busy} data-testid="fo-ai">{busy ? "Generating…" : "Generate"}</Button>
            </div>
            {ai ? <p className="text-sm leading-relaxed" data-testid="fo-ai-text">{ai}</p> : <p className="text-sm text-muted-foreground">Generate a CFO-level summary of carbon-cost exposure, offset coverage and the recommended financial action.</p>}
          </div>
        </TabsContent>

        <TabsContent value="projection" className="mt-4">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3">Carbon cost projection 2024–2030 (S$)</div>
            {(d.carbon_cost_projection || []).every((p) => !p.statutory_sgd && !p.shadow_sgd) ? (
              <div className="py-12 px-4 text-center" data-testid="fo-projection-empty">
                <Banknote className="h-8 w-8 mx-auto mb-3 text-muted-foreground/60" />
                <h3 className="font-display text-lg font-bold">Nothing to project yet</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  No internal carbon price is set{tax.is_taxable ? "" : ", and your Scope 1 emissions are below the 25,000 tCO₂e statutory carbon-tax threshold"} — so both the statutory and internal/shadow cost curves are zero.
                </p>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Set an internal carbon price to price your Scope 1+2 emissions and populate the shadow-cost curve.
                </p>
                <Button variant="primary" size="sm" className="mt-4" onClick={() => setTab("icp")} data-testid="fo-projection-set-icp">
                  <Banknote className="h-3.5 w-3.5" /> Set an internal carbon price
                </Button>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={d.carbon_cost_projection} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <RTooltip formatter={(v) => formatSgd(v)} contentStyle={{ background: "hsl(var(--surface))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="shadow_sgd" name="Internal/shadow carbon cost" fill="#00E65B" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="statutory_sgd" name="Statutory carbon tax" fill="#FFB800" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-muted-foreground mt-2">Statutory cost applies only above the 25,000 tCO₂e threshold; the internal/shadow cost prices Scope 1+2 at your internal carbon price (with annual escalation) to steer investment decisions.</p>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="risk" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <p className="text-[11px] text-muted-foreground px-4 pt-3" data-testid="fo-risk-note">
              Total financial exposure across every Risk Register entry that carries a dollar impact — the sum below reconciles with the Risk Register.
            </p>
            <Table>
              <TableHeader><TableRow><TableHead>Risk</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Financial exposure</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.financial_risk.items.map((r, idx) => (
                  <TableRow key={idx} data-testid={`fo-risk-row-${idx}`}>
                    <TableCell className="text-sm font-medium">{r.title}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.category}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSgd(r.exposure_sgd)}</TableCell>
                  </TableRow>
                ))}
                {d.financial_risk.items.length > 0 && (
                  <TableRow data-testid="fo-risk-total">
                    <TableCell className="text-sm font-semibold">Total risk-register exposure</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">{formatSgd(d.financial_risk.total_exposure_sgd)}</TableCell>
                  </TableRow>
                )}
                {!d.financial_risk.items.length && <TableRow><TableCell colSpan={3} className="text-center py-10 text-muted-foreground text-sm">No financial exposure recorded in the Risk Register.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="icp" className="mt-4">
          <div className="-mx-6 lg:-mx-8 -mt-4" data-testid="fo-icp-embedded">
            <FeatureGate featureKey="carbon_tax"><ICPOffset /></FeatureGate>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
